import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import statesList from "../util/statesList";
import { useAcceptJs } from "react-acceptjs";
import Cards from "react-credit-cards";
import "react-credit-cards/es/styles-compiled.css";
import ApiService from "../ApiService";
import PaymentReviewModal from "./Modal/Payment/PaymentReview";
import PaymentCompleteModal from "./Modal/Payment/PaymentCompleteModal";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import Spinner from "react-bootstrap/Spinner";

const authData = {
  apiLoginID: process.env.REACT_APP_AUTHORIZE_APP_ID,
  clientKey: process.env.REACT_APP_AUTHORIZE_CLIENT_KEY
};

const getCardData = card => {
  const expiryDate = card.expDate.split("/");
  return {
    cardNumber: card.number.replace(/\s/g, ""),
    cardCode: card.cvc,
    month: expiryDate[0],
    year: expiryDate[1]
  };
};

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const CollectPayment = () => {
  const location = useLocation();
  const navigate = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const { dispatchData, loading, error } = useAcceptJs({ authData });

  const ref = searchParams.get("ref");
  const id = searchParams.get("id");

  const data = location?.state;

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackText, setSnackText] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("");
  const [customer, setCustomer] = useState({
    firstName: data?.firstName || "",
    lastName: data?.lastName || "",
    phone: data?.phone || "",
    street: data?.email || "",
    apartment: data?.phone || "",
    city: data?.address || "",
    state: data?.state || null,
    zipCode: data?.zipCode || "",
    phoneNumber: data?.phoneNumber || ""
  });
  const [card, setCard] = useState({
    name: "",
    number: "",
    expDate: "",
    cvc: "",
    focused: ""
  });
  const [discount, setDiscount] = useState({
    code: "",
    amount: 0,
    isApplied: false
  });
  const [paymentData, setPaymentData] = useState({});

  const [validated, setValidated] = useState(false);
  const [errorMsgs, setErrorMsgs] = useState({});
  const [showPaymentReviewModal, setShowPaymentReviewModal] = useState(false);
  const [paymentCompleteModal, setPaymentCompleteModal] = useState(false);
  const [reviewPaymentBttnLoading, setReviewPaymentBttnLoading] = useState(false);

  useEffect(() => {
    // create async function to fetch data
    const fetchData = async () => {
      try {
        const response = await ApiService.getBookingOrderSummary(id);
        setPaymentData(response.data);
      } catch (error) {
        console.error("Error:", error);
      }
    };
    fetchData().then(r => r);
  }, [id]);

  const handleInputChange = event => {
    const { name, value } = event.target;
    setCustomer(prevState => ({ ...prevState, [name]: value }));
  };

  const handleCardNumChange = event => {
    let { name, value } = event.target;

    // Remove all non-numeric characters from the input value
    value = value.replace(/\D/g, "");

    //  add space after every 4 digits
    value = value.replace(/(.{4})/g, "$1 ").trim();

    // check if the value is 16 digits long
    if (value.length > 19) {
      value = value.slice(0, 19);
    }
    // Update the state with the formatted value
    setCard(prevState => ({ ...prevState, [name]: value }));
  };

  const handleExpirationDateChange = event => {
    let { name, value } = event.target;

    // Remove all non-numeric characters from the input value
    value = value.replace(/\D/g, "");

    // Format the value to "MM/YY"
    if (value.length > 2) {
      value = value.slice(0, 2) + "/" + value.slice(2);
    }

    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setCard(prevState => ({ ...prevState, [name]: value }));
  };

  const handleCVCChange = event => {
    let { name, value } = event.target;

    // Remove all non-numeric characters from the input value
    value = value.replace(/\D/g, "");

    // Format the value to be a 3- or 4-digit CVC code
    if (value.length > 4) {
      value = value.slice(0, 4);
    }

    setCard(prevState => ({ ...prevState, [name]: value }));
  };

  const [checkOutRes, setCheckOutRes] = useState(null);
  const [authCreation, setAuthCreation] = useState(null);

  const handleSubmit = async event => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    try {
      setReviewPaymentBttnLoading(true);
      const createCheckOutResponse = await ApiService.createCheckoutClinician({
        tbdBookId: id,
        billingAddress: {
          streetAddress1: customer?.street,
          streetAddress2: customer?.apartment,
          city: customer?.city,
          countryArea: customer?.state,
          postalCode: customer?.zipCode,
          country: "US",
          phone: customer?.phone,
          firstName: customer?.firstName,
          lastName: customer?.lastName
        }
      });

      if (createCheckOutResponse.data?.isSuccess && createCheckOutResponse.data?.checkout?.token) {
        setCheckOutRes(createCheckOutResponse.data);
        try {
          const response = await dispatchData({ cardData: getCardData(card) });
          setAuthCreation(response);
          setShowPaymentReviewModal(true);
          setReviewPaymentBttnLoading(false);
        } catch (error) {
          console.error("Error:", error);
          setErrorMsgs(error);
          setSnackText(error?.messages?.message[0]?.text || "Something went wrong");
          setSnackSeverity("error");
          setSnackOpen(true);
          setReviewPaymentBttnLoading(false);
        }
      } else {
        setSnackText(createCheckOutResponse.data?.message);
        setSnackSeverity("error");
        setSnackOpen(true);
        setReviewPaymentBttnLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setErrorMsgs(error);
      setSnackText(error?.message);
      setSnackSeverity("error");
      setSnackOpen(true);
      setReviewPaymentBttnLoading(false);
    }
  };

  const [paymentReviewLoading, setPaymentReviewLoading] = useState(false);
  const handlePaymentReview = async () => {
    try {
      setPaymentReviewLoading(true);
      const res = await ApiService.payBookingWithCheckout({
        tbdBookId: id,
        checkoutToken: checkOutRes?.checkout?.token,
        paymentToken: authCreation?.opaqueData?.dataValue
        //paymentToken: 'eyJjb2RlIjoiNTBfMl8wNjAwMDUzN0VGOTU4MUFCMDc0QkQ0M0RBQUQ5RDNEMEUxMzM3NDZBNENGN0RBRjU5Mzc4MjRDMTA1RjY1RkZEMzhENjMxNUZBOEQ2MkNBNEUxOTk4MTcyRDVGMjMyQTdDRUU0RDE3IiwidG9rZW4iOiI5NjkwNTQxNjQ4MTA2NDM1NjA0NjAyIiwidiI6IjEuMSJ9'
      });

      if (res.data.success) {
        setPaymentReviewLoading(false);
        setShowPaymentReviewModal(false);
        setPaymentCompleteModal(true);
      }
    } catch (error) {
      console.log("Error:", error);
      setPaymentReviewLoading(false);
      setSnackText(error?.message);
      setSnackSeverity("error");
      setSnackOpen(true);
    }
  };
  const handleClickApplyDiscount = () => {
    setDiscount(prevState => ({ ...prevState, isApplied: true }));
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  return (
    <div
      style={{
        width: "75%",
        margin: "auto"
      }}
    >
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity}>
          {snackText}
        </Alert>
      </Snackbar>
      <Container fluid className="text-left pb-0 mt-4 ">
        <PaymentReviewModal
          show={showPaymentReviewModal}
          onHide={() => setShowPaymentReviewModal(false)}
          paymentData={paymentData}
          customer={customer}
          onCompletePayment={handlePaymentReview}
          paymentReviewLoading={paymentReviewLoading}
        />
        <PaymentCompleteModal
          show={paymentCompleteModal}
          onHide={() => setPaymentCompleteModal(false)}
          paymentData={paymentData}
          customer={customer}
          onViewDetails={() => {
            navigate.push(`/BookingDetails?bookingId=${id}`);
          }}
        />

        <Form id="collectPayment" noValidate={true} validated={validated} onSubmit={handleSubmit}>
          <Row>
            <Col md={7}>
              <h2 className="fs-4 fw-bold mb-4 mt-2">Billing Address</h2>

              <Row className="justify-content-between">
                <Col md={6}>
                  <Form.Group controlId="fname">
                    <Form.Label style={{ fontSize: "1rem" }} className="fw-bold ">
                      First Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="John"
                      name="firstName"
                      value={customer.firstName}
                      onChange={handleInputChange}
                      onFocus={() => {
                        setCard(prevState => ({ ...prevState, focused: "name" }));
                      }}
                      required
                    />
                    <Form.Control.Feedback type="invalid">Please provide a valid first name.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="lname">
                    <Form.Label style={{ fontSize: "1rem" }} className="fw-bold ">
                      Last Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Doe"
                      name="lastName"
                      value={customer.lastName}
                      onChange={handleInputChange}
                      onFocus={() => {
                        setCard(prevState => ({ ...prevState, focused: "name" }));
                      }}
                      required
                    />
                    <Form.Control.Feedback type="invalid">Please provide a valid last name.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="address">
                    <Form.Label style={{ fontSize: "1rem" }} className="fw-bold">
                      Phone Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. +1 234 567 8901"
                      name="phone"
                      value={customer.phone}
                      onChange={handleInputChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">Please provide a valid street address.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="address">
                    <Form.Label style={{ fontSize: "1rem" }} className="fw-bold">
                      Street
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 92308 23st"
                      name="street"
                      value={customer.street}
                      onChange={handleInputChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">Please provide a valid street address.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="address">
                    <Form.Label style={{ fontSize: "1rem" }} className="fw-bold">
                      Apartment, suite no, etc.
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 392"
                      name="apartment"
                      value={customer.apartment}
                      onChange={handleInputChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">Please provide a valid apartment, suite no, etc.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="address">
                    <Form.Label style={{ fontSize: "1rem" }} className="fw-bold">
                      City
                    </Form.Label>
                    <Form.Control type="text" placeholder="e.g. Las Vegas" name="city" value={customer.city} onChange={handleInputChange} required />
                    <Form.Control.Feedback type="invalid">Please provide a valid city.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="states">
                    <Form.Label style={{ fontSize: "1rem" }} className="fw-bold">
                      State
                    </Form.Label>
                    <Form.Control as="select" name="state" value={customer.state} onChange={handleInputChange} required>
                      {statesList.map((state, index) => {
                        return (
                          <option key={index} value={state.value}>
                            {state.value}
                          </option>
                        );
                      })}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">Please provide a valid state.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="zipCode">
                    <Form.Label style={{ fontSize: "1rem" }} className="fw-bold">
                      Zip Code
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter zip code"
                      name="zipCode"
                      value={customer.zipCode}
                      onChange={handleInputChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">Please provide a valid zip code.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <h2 className="fs-4 fw-bold mb-4 mt-2">Payment</h2>
                </Col>
                <Row>
                  <Col md={6}>
                    <Cards
                      cvc={card.cvc}
                      expiry={card.expDate}
                      focused={card.focused}
                      name={customer.firstName + " " + customer.lastName}
                      number={card.number}
                    />
                  </Col>
                  <Col>
                    <Form
                      id="collectPayment"
                      noValidate
                      validated={validated}
                      onSubmit={handleSubmit}
                      className="d-flex flex-column justify-content-between h-100"
                    >
                      <Form.Group controlId="cardNumber">
                        <Form.Label style={{ fontSize: "1rem" }} className="fw-bold">
                          Card Number
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="XXXX-XXXX-XXXX-XXXX"
                          name="number"
                          value={card.number}
                          onChange={handleCardNumChange}
                          onFocus={() => {
                            setCard({ ...card, focused: "number" });
                          }}
                          required
                        />
                        <Form.Control.Feedback type="invalid">Please enter a valid 16 digit card number.</Form.Control.Feedback>
                      </Form.Group>
                      <Row>
                        <Col md={6}>
                          <Form.Group controlId="expDate">
                            <Form.Label style={{ fontSize: "1rem" }} className="fw-bold">
                              Expiration date
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="expDate"
                              placeholder="MM/YY"
                              value={card.expDate}
                              onChange={handleExpirationDateChange}
                              pattern="^(0[1-9]|1[0-2])\/([0-9]{2})$"
                              required
                              onFocus={() => {
                                setCard({ ...card, focused: "expiry" });
                              }}
                            />

                            <Form.Control.Feedback type="invalid">Please enter a valid expiration date (MM/YY).</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="cvc">
                            <Form.Label style={{ fontSize: "1rem" }} className="fw-bold">
                              CVV
                            </Form.Label>
                            <Form.Control
                              type="password"
                              placeholder="Enter the CVV code"
                              pattern="^[0-9]{3,4}$"
                              name="cvc"
                              value={card.cvc}
                              onChange={handleCVCChange}
                              required
                              onFocus={() => {
                                setCard({ ...card, focused: "cvc" });
                              }}
                            />
                            <Form.Control.Feedback type="invalid">Please provide valid CVV.</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Form>
                  </Col>
                </Row>
              </Row>
            </Col>

            <Col md={4} className="ml-5">
              <h2 className="fs-4 fw-bold mb-4 mt-2">Order Summary</h2>
              <div
                className="d-flex justify-content-between"
                style={{
                  fontSize: "1.1rem"
                }}
              >
                <p className="fs-6 fw-bold">Reference</p>
                <p className="fs-6 fw-bold">{ref}</p>
              </div>
              <div
                className="d-flex justify-content-between"
                style={{
                  fontSize: "1.1rem"
                }}
              >
                <p className="fs-6 fw-bold">Reference Id</p>
                <p className="fs-6 fw-bold">{id}</p>
              </div>
              <hr></hr>
              {/* <Row>
                <Col className="d-flex justify-content-center align-items-center">
                  <Form.Group controlId="discount">
                    <Form.Label style={{ fontSize: "1rem" }} className="fw-bold">
                      Apply Discount
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter discount code"
                      name="discount"
                      value={discount.code}
                      onChange={e => {
                        setDiscount({ ...discount, code: e.target.value });
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col className="d-flex justify-content-end align-items-center mt-4 pb-0">
                  <Button variant="outline-primary" onClick={handleClickApplyDiscount}>
                    {" "}
                    Apply{" "}
                  </Button>
                </Col>
              </Row> */}
              {/* Text field with disoint code and corss icon and disable wth green border */}

              {/* <hr></hr> */}

              <Container className="w-100">
                <Col>
                  {paymentData?.unpaidProducts?.map((product, index) => {
                    return (
                      <Row className="d-flex justify-content-between align-items-center">
                        <Row>
                          <h5 className="fw-bold">{product.qty} </h5> <h5 className="ml-1">X</h5>
                          <h5 className="ml-2">{product.name ? product.name : product.slug}</h5>
                        </Row>
                        <Row>
                          <h5>$ {product.unitPrice * product.qty}</h5>
                        </Row>
                      </Row>
                    );
                  })}
                </Col>
              </Container>

              <hr></hr>
              {/* add font weight semi bold */}
              <div
                className="d-flex justify-content-between"
                style={{
                  fontSize: "1.1rem"
                }}
              >
                <p className="fs-6 fw-bold">Sub-Total</p>
                <p className="fs-6 fw-bold">${paymentData?.netTotalUnpaid}</p>
              </div>
              <div
                className="d-flex justify-content-between"
                style={{
                  fontSize: "1.1rem"
                }}
              >
                <p className="fs-6 fw-bold">Tax</p>
                <p className="fs-6 fw-bold">${paymentData?.taxUnPaid || "0"}</p>
              </div>
              {/* <div
                className="d-flex justify-content-between"
                style={{
                  fontSize: "1.1rem"
                }}
              >
                <p className="fs-6 fw-bold">Total Savings</p>
                <p className="fs-6 fw-bold">${"0"}</p>
              </div> */}
              <div
                className="d-flex justify-content-between"
                style={{
                  fontSize: "1.1rem"
                }}
              >
                <p className="fs-6 fw-bold">Total</p>
                <p className="fs-6 fw-bold">${paymentData?.grossTotalUnpaid}</p>
              </div>
              <hr></hr>
              <Row>
                <Col md={12} className="d-flex justify-content-center align-items-center">
                  <Button
                    variant="primary"
                    type="submit"
                    form="collectPayment"
                    className="w-100"
                    disabled={reviewPaymentBttnLoading}
                    style={{
                      height: "2.5rem",

                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    }}
                  >
                    {reviewPaymentBttnLoading ? <Spinner animation="border" role="status" size="sm"></Spinner> : "Review Payment"}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default CollectPayment;
